import { useEffect, useMemo, type ReactElement } from 'react';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AppThemeProvider } from '@/shared/theme/AppThemeProvider';
import { ToastProvider } from '@/shared/ui';
import { createPortalQueryClient } from './api/portalQueryClient';
import { setSessionExpiredHandler } from './auth/portalSession';
import { PortalRoutes } from './router';

/** Registers the global session-expiry redirect once the router's own
 * `navigate` is available — must live INSIDE `<BrowserRouter>`. */
function SessionExpiryHandlerRegistration(): null {
  const navigate = useNavigate();
  useEffect(() => {
    setSessionExpiredHandler(() => navigate('/sign-in', { replace: true }));
    return () => setSessionExpiredHandler(null);
  }, [navigate]);
  return null;
}

export function PortalApp(): ReactElement {
  const queryClient = useMemo(() => createPortalQueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider>
        <ToastProvider>
          <BrowserRouter>
            <SessionExpiryHandlerRegistration />
            <PortalRoutes />
          </BrowserRouter>
        </ToastProvider>
      </AppThemeProvider>
    </QueryClientProvider>
  );
}
